/**
 * /tools/scrambled-virtues?clientId=X — F-033 (2026-05-25).
 *
 * The unconscious virtue-finding puzzle. Letters are scrambled in a
 * fixed grid (one canonical layout per locale, ~30 virtues hidden in
 * horizontal / vertical / diagonal lines). The client press-and-drags
 * the mouse across letters to mark a word they spot; on release, if
 * the path matches one of the hidden virtues, the cells highlight
 * permanently and the virtue lands in the "Found virtues" box on
 * the right. Each chip there has a ✕ — clicking it removes the
 * virtue from the box AND un-highlights the cells, so a virtue that
 * doesn't resonate doesn't carry forward.
 *
 * The payload conforms to the existing `scrambled-virtues` Zod schema
 * (apps/api/src/tools/schemas/scrambled-virtues.ts): one entry per
 * grid word with `isFound` flipped and `recognitionOrder` set in find
 * order. The auto-ingest hook (F-028) then surfaces every found
 * virtue into the Carpe Diem Pool with source_tool_key=`scrambled-virtues`
 * so the coach sees the provenance in the matrix Pool rail.
 *
 * The grid is a static asset (one per locale) imported as JSON, so
 * every client sees the SAME puzzle — apples-to-apples comparisons
 * across cohorts, and the analytics ("which virtues do clients find
 * first") stay meaningful.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { usePristine } from '@/lib/use-pristine';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';
import gridEn from './scrambled-virtues-grid.en.json';
import gridDe from './scrambled-virtues-grid.de.json';

interface GridPlacement {
  word: string;
  row: number;
  col: number;
  dr: number;
  dc: number;
  length: number;
}

interface GridData {
  size: number;
  locale: string;
  cells: string[][];
  words: GridPlacement[];
}

const GRIDS: Record<'en' | 'de', GridData> = {
  en: gridEn as GridData,
  de: gridDe as GridData,
};

/** A canonical (row,col) string key — used in Set<string> for highlights. */
function cellKey(row: number, col: number): string {
  return `${row},${col}`;
}

/** Generate the cells a placement occupies. */
function placementCells(p: GridPlacement): string[] {
  const out: string[] = [];
  for (let i = 0; i < p.length; i++) {
    out.push(cellKey(p.row + p.dr * i, p.col + p.dc * i));
  }
  return out;
}

/**
 * Snap (anchor → cursor) to one of the 8 valid axes (horizontal,
 * vertical, two diagonals — both directions) and return the cells
 * along that line up to the cursor's projection. Returns the canonical
 * cells from anchor to the snapped end.
 */
function snapPath(
  anchor: { row: number; col: number },
  cursor: { row: number; col: number },
): { row: number; col: number }[] {
  const dRow = cursor.row - anchor.row;
  const dCol = cursor.col - anchor.col;
  const adRow = Math.abs(dRow);
  const adCol = Math.abs(dCol);
  if (dRow === 0 && dCol === 0) {
    return [anchor];
  }
  // Pick the axis closest to the cursor movement.
  let stepR = 0;
  let stepC = 0;
  let len = 1;
  if (adRow >= adCol * 2) {
    // Vertical-dominant → pure vertical.
    stepR = dRow > 0 ? 1 : -1;
    len = adRow + 1;
  } else if (adCol >= adRow * 2) {
    // Horizontal-dominant → pure horizontal.
    stepC = dCol > 0 ? 1 : -1;
    len = adCol + 1;
  } else {
    // Diagonal — both axes move together.
    stepR = dRow > 0 ? 1 : -1;
    stepC = dCol > 0 ? 1 : -1;
    len = Math.max(adRow, adCol) + 1;
  }
  const cells: { row: number; col: number }[] = [];
  for (let i = 0; i < len; i++) {
    cells.push({ row: anchor.row + stepR * i, col: anchor.col + stepC * i });
  }
  return cells;
}

/**
 * Test whether a sequence of cells corresponds to a hidden word
 * (forward or backward). Returns the matched placement + the canonical
 * cell-key list (always in placement order) when it does, else null.
 */
function matchPath(
  cells: { row: number; col: number }[],
  words: GridPlacement[],
): { placement: GridPlacement; cellKeys: string[] } | null {
  if (cells.length < 2) return null;
  const forwardKeys = cells.map((c) => cellKey(c.row, c.col)).join('|');
  const reverseKeys = [...cells].reverse().map((c) => cellKey(c.row, c.col)).join('|');
  for (const p of words) {
    const placementKeys = placementCells(p).join('|');
    if (placementKeys === forwardKeys || placementKeys === reverseKeys) {
      return { placement: p, cellKeys: placementCells(p) };
    }
  }
  return null;
}

interface FoundState {
  word: string;
  order: number;
}

export function ScrambledVirtuesPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const locale: 'en' | 'de' = i18n.language === 'de' ? 'de' : 'en';
  const grid = GRIDS[locale];

  const [found, setFound] = useState<FoundState[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  // The found list IS the form. Once saved, the Save button disables
  // until the client finds/removes a virtue (F-007: button state = the
  // saved confirmation).
  const { isPristine, markSaved } = usePristine<FoundState[]>([]);

  // Selection state — captured during drag. `anchor` is the cell where
  // mousedown happened; `cursor` is the cell currently under the
  // pointer. Mirrored into refs so the onMouseEnter handler reads the
  // anchor synchronously (state updates between mousedown + the next
  // mouseenter are batched by React, so the closure would otherwise
  // see `anchor === null` for fast drags).
  const [anchor, setAnchor] = useState<{ row: number; col: number } | null>(null);
  const [cursor, setCursor] = useState<{ row: number; col: number } | null>(null);
  const anchorRef = useRef<{ row: number; col: number } | null>(null);
  const cursorRef = useRef<{ row: number; col: number } | null>(null);

  // Highlighted cells (permanent — every found word's cells).
  const highlightedCells = useMemo(() => {
    const set = new Set<string>();
    for (const f of found) {
      const placement = grid.words.find((w) => w.word === f.word);
      if (placement) {
        for (const key of placementCells(placement)) set.add(key);
      }
    }
    return set;
  }, [found, grid.words]);

  // Cells currently under the live drag path (transient — flame tint).
  const draggingCells = useMemo(() => {
    if (!anchor || !cursor) return new Set<string>();
    const path = snapPath(anchor, cursor);
    return new Set(path.map((c) => cellKey(c.row, c.col)));
  }, [anchor, cursor]);

  /* ── Hydrate from prior completion ── */
  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'scrambled-virtues');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          if (Array.isArray(p.words)) {
            const restored: FoundState[] = (p.words as Record<string, unknown>[])
              .filter((w) => w.isFound === true && typeof w.word === 'string')
              .map((w, idx) => ({
                word: w.word as string,
                order: typeof w.recognitionOrder === 'number' ? w.recognitionOrder : idx + 1,
              }))
              // Only restore virtues that are actually in this locale's
              // grid (defensive — a payload from a prior EN session
              // shouldn't blow up the DE grid render).
              .filter((f) => grid.words.some((p2) => p2.word === f.word));
            setFound(restored);
            markSaved(restored);
          }
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, grid]);

  /* ── Drag handlers ── */
  const onCellDown = (row: number, col: number): void => {
    const cell = { row, col };
    anchorRef.current = cell;
    cursorRef.current = cell;
    setAnchor(cell);
    setCursor(cell);
  };
  const onCellEnter = (row: number, col: number): void => {
    if (!anchorRef.current) return;
    const cell = { row, col };
    cursorRef.current = cell;
    setCursor(cell);
  };

  // Window-level mouseup so a release outside any cell still resolves
  // the drag. Single effect (no anchor dep) so the listener stays
  // attached for the lifetime of the page.
  const gridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onUp = (): void => {
      const a = anchorRef.current;
      const c = cursorRef.current;
      if (a && c) {
        const path = snapPath(a, c);
        const match = matchPath(path, grid.words);
        if (match) {
          setFound((prev) =>
            prev.some((f) => f.word === match.placement.word)
              ? prev
              : [...prev, { word: match.placement.word, order: prev.length + 1 }],
          );
        }
      }
      anchorRef.current = null;
      cursorRef.current = null;
      setAnchor(null);
      setCursor(null);
    };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, [grid.words]);

  /* ── Found-virtue removal ── */
  const removeFound = (word: string): void => {
    setFound((prev) => prev.filter((f) => f.word !== word));
  };

  /* ── Submit ── */
  const canSubmit = hydrated && !submitting && found.length > 0 && !isPristine(found);

  const onSubmit = async (): Promise<void> => {
    if (!clientId || !canSubmit) return;
    setSubmitError(null);
    setSubmitting(true);
    const sorted = [...found].sort((a, b) => a.order - b.order);
    const words = sorted.map((f) => ({
      word: f.word,
      isFound: true,
      recognitionOrder: f.order,
    }));
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'scrambled-virtues',
        { payload: { words } },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
      markSaved(found);
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.scrambledVirtues.errors.generic'));
      } else {
        setSubmitError(t('tools.scrambledVirtues.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="scrambled-virtues-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.scrambledVirtues.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">
          {t('tools.scrambledVirtues.errors.missingClient')}
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-5xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.scrambledVirtues.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.scrambledVirtues.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.scrambledVirtues.description')}
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_18rem] gap-s5">
        {/* ── Grid ── */}
        <article
          ref={gridRef}
          className="rounded-lg border border-bone-3 bg-bone-2 p-s4 select-none"
          data-testid="scrambled-virtues-grid"
        >
          <div
            className="grid gap-[2px] mx-auto"
            style={{
              gridTemplateColumns: `repeat(${grid.size}, minmax(0, 1fr))`,
              maxWidth: `${grid.size * 2}rem`,
            }}
          >
            {grid.cells.map((row, r) =>
              row.map((letter, c) => {
                const key = cellKey(r, c);
                const isHighlighted = highlightedCells.has(key);
                const isDragging = draggingCells.has(key);
                return (
                  <button
                    key={key}
                    type="button"
                    data-row={r}
                    data-col={c}
                    data-testid={`scrambled-cell-${r}-${c}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onCellDown(r, c);
                    }}
                    onMouseEnter={() => onCellEnter(r, c)}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      onCellDown(r, c);
                    }}
                    className={`aspect-square flex items-center justify-center font-mono text-sm font-semibold uppercase rounded-sm cursor-pointer transition-colors ${
                      isHighlighted
                        ? 'bg-flame text-bone'
                        : isDragging
                          ? 'bg-flame-glow text-flame-deep'
                          : 'bg-bone text-ink hover:bg-flame-glow/40'
                    }`}
                    aria-label={`${letter} row ${r + 1} column ${c + 1}`}
                  >
                    {letter}
                  </button>
                );
              }),
            )}
          </div>
        </article>

        {/* ── Found virtues box ── */}
        <aside className="rounded-lg border border-bone-3 bg-bone-2 p-s4">
          <h2 className="font-display text-display-sm text-ink mb-s1">
            {t('tools.scrambledVirtues.found.title')}
          </h2>
          <p className="text-xs text-gray-1 mb-s3">
            {t('tools.scrambledVirtues.found.hint', { found: found.length, total: grid.words.length })}
          </p>
          {found.length === 0 ? (
            <p
              className="text-sm text-gray-2 italic"
              data-testid="scrambled-virtues-found-empty"
            >
              {t('tools.scrambledVirtues.found.empty')}
            </p>
          ) : (
            <ul className="space-y-s2" data-testid="scrambled-virtues-found-list">
              {found
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((f) => (
                  <li
                    key={f.word}
                    className="flex items-center justify-between rounded-md border border-flame-deep/40 bg-flame-glow/30 px-s3 py-s2 text-flame-deep"
                    data-testid={`scrambled-virtues-found-${f.word}`}
                  >
                    <span className="font-mono text-sm font-semibold">{f.word}</span>
                    <button
                      type="button"
                      onClick={() => removeFound(f.word)}
                      className="text-flame-ember hover:text-flame text-sm font-bold"
                      aria-label={t('tools.scrambledVirtues.found.removeAria', { word: f.word })}
                      data-testid={`scrambled-virtues-remove-${f.word}`}
                    >
                      ✕
                    </button>
                  </li>
                ))}
            </ul>
          )}

          <div className="mt-s5 space-y-s3">
            {submitError && (
              <p
                role="alert"
                className="form-error"
                data-testid="scrambled-virtues-submit-error"
              >
                {submitError}
              </p>
            )}
            {savedAt && (
              <p
                role="status"
                className="text-sm text-flame-deep"
                data-testid="scrambled-virtues-saved-at"
              >
                ✓ {t('tools.scrambledVirtues.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
              </p>
            )}
            <FinalizeWarning />
            <button
              type="button"
              onClick={() => void onSubmit()}
              className="btn-primary w-full"
              disabled={!canSubmit}
              data-testid="scrambled-virtues-submit"
            >
              {submitting
                ? t('tools.scrambledVirtues.submitting')
                : t('tools.scrambledVirtues.submit')}
            </button>
          </div>
        </aside>
      </div>
    </section>
  );
}
